/**
 * Protocolul de colaborare (SP-03, pasul 4 din 4) — partea PURA.
 *
 * Aici sta tot ce se poate verifica fara browser: `vitest.config.ts` ruleaza pe
 * `environment: 'node'`, FARA jsdom, deci testele de componenta nu sunt posibile
 * in proiectul asta. Acelasi motiv pentru care logica de stergere a iesit din
 * componenta la SP-05a (`textDupaStergere`): ce ramane inauntru nu se masoara.
 *
 * 🔴 REGULA CARE DA FORMA FISIERULUI: marimea NU se inventeaza. Se citeste din
 * raspuns, si daca nu se stie, randul **se omite**. Un „180 KB" scris de noi ar
 * fi exact tipul de detaliu care pare grija pentru om si e o minciuna mica.
 * De-aia `formateazaMarime` intoarce `null`, nu `'0 KB'` si nu `'necunoscut'`.
 *
 * ⚠️ Numele fisierului si marimea vin din locuri DIFERITE, si doar unul e
 * obligatoriu prin contract: `Content-Length` e garantat pe 200, iar
 * `Content-Disposition` il pune B2 dar il tratam ca optional — un implicit e o
 * plasa, nu o regula.
 */

/** Ce a raspuns serverul, tradus in ce arata ecranul. */
export type StareProtocol =
    | { stare: 'gata'; nume: string; marime: number | null }
    /** Ecranul 2 din directie: „nu e disponibil inca". Adevarat in ambele motive. */
    | { stare: 'indisponibil'; motiv: MotivIndisponibil }
    /**
     * Contul autentificat n-are inca o scoala aprobata (P0-4). Backendul raspunde
     * 409 din `SecurityContextUtil.requireSchoolId()` (`ContIncompletException`,
     * mesaj `cont.incomplet`) inainte sa apuce sa caute vreun protocol — nu e o
     * eroare tehnica si nu se rezolva „incearca din nou": pe ruta `own/...` un
     * profesor nou, cu cererea inca PENDING, primeste 409 de fiecare data, pana
     * cand un admin ii aproba scoala. Fara ramura asta, TOT profesor nou vedea
     * eroarea rosie de la starea `esuat`, cu sfatul fals „incearca peste cateva
     * momente" — nimic nu se schimba din reincercare.
     *
     * ⚠️ 409 e specific rutei `own/...` (apelata doar de PROFESOR): `alScolii`
     * (DBEST/ADMIN) nu cheama `requireSchoolId()`, deci nu poate produce starea
     * asta — vezi `ProtocolDeColaborareController.java`.
     */
    | { stare: 'neaprobat' }
    | { stare: 'esuat'; status: number | null };

/**
 * 🔴 Doua 404-uri diferite, care arata IDENTIC pe ecran si se disting dupa CORP.
 *
 *   corp JSON (206 octeti)  →  `ruta-inactiva`   ruta nu exista pe mediul asta
 *   corp de 0 octeti        →  `fara-protocol`   scoala nu e in editia curenta
 *
 * Ambele linii de sursa, ca sa nu mai deduca nimeni (inclusiv eu):
 *
 *   `advice/GlobalExceptionHandler.java:168` — `@ExceptionHandler(NoResourceFoundException)`
 *       intoarce `ErrorResponse("Resursa ceruta nu exista.", 404, null)`. Cu
 *       `protocol.descarcare.enabled=false`, `ProtocolDeColaborareController` nu se
 *       instantiaza (`@ConditionalOnProperty`), calea devine nemapata, si raspunsul
 *       vine de AICI — deci **cu corp**.
 *   `controller/ProtocolDeColaborareController.java:90` — `case NEGASITA ->
 *       ResponseEntity.notFound().build()`. `.build()` nu are corp si nu trece prin
 *       niciun advice (nu e o exceptie) — deci **0 octeti**.
 *
 * ⚠️ ERA SCRIS INVERS AICI, si nu dintr-o scapare de tastare: presupusesem ca „bean
 * absent ⇒ Spring raspunde 404 fara corp", am modelat presupunerea in stub, am
 * construit discriminantul pe ea si i-am scris si justificarea — fara s-o masor
 * vreodata pe un backend real. Stubul confirma premisa care il generase, deci proba
 * si codul erau de acord pentru ca veneau din aceeasi sursa: **doua lucruri care se
 * confirma reciproc si au aceeasi origine nu sunt doua dovezi, ci una singura.**
 * Un control negativ n-ar fi prins-o — ar fi fost construit pe aceeasi premisa.
 *
 * Masurat de V1 pe DEV, `70c681b`, cu control pozitiv in aceeasi rulare:
 *   flag OPRIT,  PROFESOR `/own/protocol-de-colaborare`  → 404, `application/json`, 206 octeti
 *                PROFESOR `/own/nu-exista`               → 404, IDENTIC octet cu octet
 *   flag PORNIT, DBEST `/{uuid-inexistent}/…`            → 404, `Content-Length: 0`, fara `Content-Type`
 *                DBEST `{scoala din afara editiei}`      → 404, aceeasi forma
 *                DBEST `{scoala din editia curenta}`     → 200, 12180 octeti, `%PDF-`
 *
 * Controlul pozitiv e cel care face restul sa insemne ceva: fara el, „404 cu 0 octeti"
 * era compatibil si cu „ruta nu raspunde pe DBEST", si cu „tokenul nu e bun".
 *
 * ⚠️ Cele doua forme NU pot fi observate simultan pe ruta asta, si nu e o lacuna de
 * masuratoare: cu flagul oprit, `NEGASITA` e inaccesibil (controllerul nu exista); cu
 * el pornit, `NoResourceFoundException` nu se mai produce pe calea asta. Deci
 * discriminantul nu raspunde la „care din doua rezultate a dat cererea", ci la
 * **„in ce stare e mediul"**.
 *
 * Pe ecran amandoua raman aceeasi stare — omul chiar nu poate lua protocolul acum,
 * si „ruta nu e pornita" nu e o informatie pentru un profesor.
 */
export type MotivIndisponibil = 'fara-protocol' | 'ruta-inactiva';

/**
 * POARTA cererii (owner, 2026-10-07: «doar când școala e aprobată»): protocolul se cere DOAR cu
 * claim-ul `schoolApprovedForCurrentEdition === true` in token.
 *
 * De ce: `GET /api/schools/own/protocol-de-colaborare` nu e o citire nevinovata — fiecare 200
 * consemneaza protocolul ca DESCARCAT (`protocolDescarcatDt`), iar inainte de aprobare ruta raspunde
 * oricum doar 404 (scoala nu e inca in editia curenta) sau 409 (contul n-are scoala). Masurat pe
 * 14 zile: 540 de 404 si 41 de 409, toate cereri al caror raspuns se stia din token.
 *
 * `=== true`, nu „truthy": un token vechi, fara claim, se trateaza ca neaprobat — nu se cere nimic.
 */
export function protocolulSePoateCere(
    user: { schoolApprovedForCurrentEdition?: boolean } | null | undefined
): boolean {
    return user?.schoolApprovedForCurrentEdition === true;
}

/**
 * Starea ecranului cand poarta e inchisa: aceeasi „neaprobat" pe care o dadea 409-ul, doar ca
 * stiuta din token, FARA cerere. Nu e eroare si nu se rezolva prin reincercare.
 */
export const STARE_NEAPROBAT: StareProtocol = { stare: 'neaprobat' };

/** `tu` pe cardul de inscriere si in meniu; `dumneavoastra` pe «Acasă». */
export type RegistruProtocol = 'tu' | 'dumneavoastra';

export interface MesajProtocol {
    /** `eroare` doar pentru ce a esuat tehnic; o stare de business nu se arata cu rosu. */
    ton: 'info' | 'eroare';
    /** Ce s-a intamplat si ce poate face omul acum. */
    text: string;
    /** `true` cand o noua incercare poate schimba rezultatul. */
    sePoateReincerca: boolean;
    /**
     * Indemnul de a suna la ECOTIC, FARA numar: apelantul lipeste numarul dupa el (link `tel:` unde
     * se poate, text intr-un toast). `null` cand un telefon n-ar ajuta.
     */
    indemnContact: string | null;
}

/**
 * Ce i se spune omului cand protocolul NU se poate descarca: ce s-a intamplat + ce poate face.
 * `null` pentru „gata" — acolo nu e nimic de explicat.
 *
 * Textele stau aici, nu in JSX, ca sa fie testabile (`environment: 'node'`, fara jsdom) si ca cele
 * trei locuri care le arata (cardul, «Acasă», meniul) sa nu spuna trei lucruri diferite.
 */
export function mesajStareProtocol(
    stare: StareProtocol,
    registru: RegistruProtocol
): MesajProtocol | null {
    const formal = registru === 'dumneavoastra';
    const suna = formal ? 'sunați la ECOTIC:' : 'sună la ECOTIC:';

    switch (stare.stare) {
        case 'gata':
            return null;
        case 'neaprobat':
            return {
                ton: 'info',
                text: formal
                    ? 'Protocolul devine disponibil după ce școala dumneavoastră este aprobată.'
                    : 'Protocolul devine disponibil după ce școala ta este aprobată.',
                sePoateReincerca: false,
                indemnContact: null
            };
        case 'indisponibil':
            // Ambele motive (`fara-protocol`, `ruta-inactiva`) spun omului acelasi lucru — vezi
            // `MotivIndisponibil`. Cu poarta de mai sus, aici ajunge doar o scoala APROBATA, deci
            // nu mai e „inca nepublicat": e ceva de semnalat echipei.
            return {
                ton: 'info',
                text: formal
                    ? 'Protocolul școlii dumneavoastră nu este disponibil momentan. Încercați din nou mai târziu.'
                    : 'Protocolul școlii tale nu este disponibil momentan. Încearcă din nou mai târziu.',
                sePoateReincerca: true,
                indemnContact: `Dacă tot nu apare, ${suna}`
            };
        case 'esuat':
            // `status === null` = cererea n-a ajuns la server (retea cazuta, cerere anulata).
            if (stare.status === null) {
                return {
                    ton: 'eroare',
                    text: formal
                        ? 'Nu am putut contacta serverul. Verificați conexiunea la internet și încercați din nou.'
                        : 'Nu am putut contacta serverul. Verifică conexiunea la internet și încearcă din nou.',
                    sePoateReincerca: true,
                    indemnContact: null
                };
            }
            return {
                ton: 'eroare',
                text: formal
                    ? 'Protocolul nu a putut fi descărcat acum. Încercați din nou peste câteva momente.'
                    : 'Protocolul nu a putut fi descărcat acum. Încearcă din nou peste câteva momente.',
                sePoateReincerca: true,
                indemnContact: `Dacă problema persistă, ${suna}`
            };
    }
}

/** Mesajul intr-un singur sir, cu numarul lipit — pentru locurile fara link (toast). */
export function textMesajProtocol(mesaj: MesajProtocol, telefon: string): string {
    const numar = telefon.trim();
    if (!mesaj.indemnContact || !numar) return mesaj.text;
    return `${mesaj.text} ${mesaj.indemnContact} ${numar}.`;
}

/** Numele afisat cat timp serverul nu trimite altul. Al EDITIEI, nu al scolii. */
export const NUME_IMPLICIT = 'Protocol de colaborare';

/**
 * `Content-Length`, sau `null` daca nu se poate sti.
 *
 * Respinge tot ce nu e un numar pozitiv finit: antetul lipsa, text, `0`
 * (un PDF de zero octeti nu e un PDF), negativ. Toate inseamna acelasi lucru
 * pentru ecran — randul de marime nu se deseneaza.
 */
export function marimeDinRaspuns(headers: Headers): number | null {
    const brut = headers.get('Content-Length');
    if (brut === null || brut.trim() === '') return null;
    const n = Number(brut);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
}

/**
 * Marimea, in romaneste, sau `null` cand nu se stie.
 *
 * `null` in loc de un sir de rezerva: apelantul trebuie sa poata OMITE randul,
 * iar un `'—'` intors de aici ar face imposibila distinctia intre „nu stim" si
 * „stim, si e mic".
 */
export function formateazaMarime(octeti: number | null): string | null {
    if (octeti === null || !Number.isFinite(octeti) || octeti <= 0) return null;
    if (octeti < 1024 * 1024) {
        // Sub 1 KB rotunjim in sus: „0 KB" ar arata ca o eroare, desi fisierul exista.
        return `${Math.max(1, Math.round(octeti / 1024))} KB`;
    }
    // Virgula zecimala, nu punct: textul e romanesc.
    return `${(octeti / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Numele din `Content-Disposition`, cu implicit cand antetul lipseste sau nu se
 * potriveste. Accepta si forma `filename*=UTF-8''…` (RFC 5987), pentru ca numele
 * contine diacritice romanesti si serverele le trimit adesea procent-codate.
 */
export function numeFisier(dispozitie: string | null, implicit: string = NUME_IMPLICIT): string {
    if (!dispozitie) return implicit;

    const extins = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(dispozitie);
    if (extins) {
        try {
            const decodat = decodeURIComponent(extins[1].trim());
            if (decodat) return decodat;
        } catch {
            // Procent-codare stricata: cade pe forma simpla, apoi pe implicit.
        }
    }

    const simplu = /filename\s*=\s*"?([^";]+)"?/i.exec(dispozitie);
    const nume = simplu?.[1]?.trim();
    return nume ? nume : implicit;
}

/**
 * Statusul + corpul, traduse in starea ecranului.
 *
 * `areCorp` vine din apelant (care a citit deja raspunsul), nu se ghiceste aici:
 * modulul ramane pur si testabil fara `fetch`.
 */
export function stareDinRaspuns(
    status: number,
    headers: Headers,
    areCorp: boolean
): StareProtocol {
    if (status === 200) {
        return {
            stare: 'gata',
            nume: numeFisier(headers.get('Content-Disposition')),
            marime: marimeDinRaspuns(headers)
        };
    }
    if (status === 404) {
        // Corp ⇒ handlerul global ⇒ ruta nu exista. Fara corp ⇒ `.build()` din controller
        // ⇒ 404 de business. Vezi cele doua linii de sursa de la `MotivIndisponibil`.
        return { stare: 'indisponibil', motiv: areCorp ? 'ruta-inactiva' : 'fara-protocol' };
    }
    if (status === 409) {
        // `ContIncompletException` (cont.incomplet) — scoala profesorului inca nu e
        // aprobata. Stare de business, nu eroare tehnica: vezi comentariul de pe
        // `StareProtocol['neaprobat']`.
        return { stare: 'neaprobat' };
    }
    // 403 ajunge tot aici, deliberat: ruta e deja pazita pe rol in frontend, deci
    // un 403 inseamna ca ceva nu se potriveste intre cele doua parti — o eroare
    // tehnica de aratat, nu o stare de business de explicat omului.
    return { stare: 'esuat', status };
}
